<template>
    <div class="wrapper w-100 h-100 d-flex justify-center align-center">
        <v-card class="login-card" elevation="6" rounded="xl">
            <v-card-text class="pa-8 pa-sm-10">
                <!-- Logo -->
                <div class="text-center mb-6">
                    <router-link class="d-inline-flex align-center text-decoration-none" to="/">
                        <v-img :src="logoFull" :width="200" draggable="false"/>
                        <span class="font-weight-bold font-italic text-h4 text-primary ms-1">Go</span>
                    </router-link>
                </div>

                <div class="text-center mb-4">
                    <div class="verify-icon-circle mx-auto mb-3">
                        <v-icon size="36" color="primary">mdi-email-outline</v-icon>
                    </div>
                    <h2 class="text-h5 font-weight-bold mb-1">Verify your email</h2>
                    <p class="text-body-2 text-medium-emphasis">
                        Enter the 6-digit code we sent to your email
                    </p>
                </div>

                <v-form ref="form" v-model="formData.valid" validate-on="input lazy" @submit.prevent="submit">
                    <v-text-field v-model="formData.code" :rules="codeRules"
                                  autofocus color="primary" density="comfortable"
                                  inputmode="numeric" maxlength="6"
                                  label="6-digit code" placeholder="000000"
                                  prepend-inner-icon="mdi-shield-key-outline"
                                  rounded="lg" variant="outlined"
                                  @input="onCodeInput"/>

                    <v-btn :disabled="!formData.valid || processing" :loading="processing" block color="primary"
                           class="mb-4" rounded="lg" size="large" @click="submit">
                        Verify
                    </v-btn>

                    <div class="text-center">
                        <span v-if="resendCooldown > 0" class="text-caption text-medium-emphasis">
                            Resend code in {{ resendCooldown }}s
                        </span>
                        <a v-else class="text-caption text-primary text-decoration-none font-weight-bold cursor-pointer"
                           @click="resendCode">
                            Resend Verification Code
                        </a>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>

        <!-- Footer -->
        <div class="login-footer text-caption text-white">
            &copy; {{ new Date().getFullYear() }} SimpliGPS Go by NextEra IoT
        </div>
    </div>
</template>

<script setup>
import {useRouter} from "vue-router";
import {onBeforeUnmount, onMounted, reactive, ref} from 'vue';
import {send} from "@/composables/apiClient";
import {showApiError} from "@/composables/useApiError";
import logoFull from "@/assets/images/logo-full.png"
import {useNavigation} from "@/composables/globals";
import {useSnackbarStore} from "@/stores/snackBarStore";

const userId = ref(null)
const router = useRouter()
const snackbar = useSnackbarStore()
const processing = ref(false)
const {navigateTo} = useNavigation()
const resendCooldown = ref(0)
let resendTimer = null
const formData = reactive({
    code: null,
    valid: false,
})

const codeRules = [
    v => !!v || 'Enter the code we emailed you',
    v => /^\d{6}$/.test(v || '') || 'Enter the 6-digit numeric code',
]

const onCodeInput = () => {
    if (formData.code) {
        formData.code = formData.code.replace(/\D/g, '').slice(0, 6)
    }
}

const startResendCooldown = (seconds = 60) => {
    resendCooldown.value = Math.max(1, Math.floor(seconds));
    if (resendTimer) clearInterval(resendTimer);
    resendTimer = setInterval(() => {
        resendCooldown.value--;
        if (resendCooldown.value <= 0) clearInterval(resendTimer);
    }, 1000);
}

const submit = () => {
    processing.value = true;
    send('put', `verify-user/${userId.value}`, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message)
            navigateTo('login')
        }).catch((error) => {
            snackbar.showSnackbar(error.response?.data?.message ?? 'Something went wrong', 'error')
        }).finally(() => processing.value = false)
}

const resendCode = () => {
    if (!userId.value) return;
    processing.value = true;
    send('post', `send-verification/${userId.value}/mail`)
        .then(() => {
            snackbar.showSnackbar('Verification code sent!');
            startResendCooldown();
        })
        .catch(function (errorResponse) {
            const info = showApiError(snackbar, errorResponse, 'Failed to send code');
            // Disable the Resend button for the throttle's retry window
            // so rapid taps don't compound the server-side counter.
            if (info.isThrottled) {
                startResendCooldown(Math.min(info.retryAfterSec || 60, 600));
            }
        }).finally(() => processing.value = false)
}

onMounted(() => {
    userId.value = router.currentRoute.value.params.user;
    if (userId.value) {
        send('post', `send-verification/${userId.value}/mail`)
            .then(() => startResendCooldown())
            .catch(function (errorResponse) {
                // Auto-send on page mount - silently swallow 429 (user
                // can still click Resend); surface real errors.
                if (errorResponse?.response?.status !== 429) {
                    showApiError(snackbar, errorResponse, "Couldn't send code. Please use Resend.");
                }
            })
    }
})

onBeforeUnmount(() => {
    if (resendTimer) clearInterval(resendTimer);
})
</script>

<style scoped>
.wrapper {
    background: url('@/assets/images/login-bg.png') rgba(38, 35, 90, 0.65);
    background-blend-mode: multiply;
    background-size: cover;
    background-position: center;
    position: relative;
}

.login-card {
    width: 100%;
    max-width: 440px;
    margin: 16px;
}

.verify-icon-circle {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: rgba(38, 35, 90, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
}

.login-footer {
    position: absolute;
    bottom: 16px;
    opacity: 0.8;
}
</style>
